
import { Component, Vue } from "vue-property-decorator"
import { namespace } from "vuex-class"

import VuePerfectScrollbar from "vue-perfect-scrollbar"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"
import api from "@/services/api"

import { Template, Area, Severity } from "@/types"
import { CaseColumn } from "@/types/caseColumns"
import { formatFieldsMixin } from "@/mixins/formatFieldsMixin"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import { Tag } from "@/types/tag"
import { Schedule, ScheduleQueue } from "@/types/schedule"
import Confirmation from "../../Confirmation.vue"
import moment from "moment"
import DataModal from "@/components/Config/DataModal.vue"
import HelpIcon from "@/components/New/Page/PageComponents/HelpIcon.vue"

const setup = namespace("setup")

@Component({
	components: {
		"help-icon": HelpIcon,
		scrollbar: VuePerfectScrollbar,
		"data-table": DataTable,
		skeleton: SkeletonLoader,
		"data-modal": DataModal,
		"confirm": Confirmation
	}
})
export default class CadenceSchedules extends Vue {
	$refs!: {
		confirmModal: Confirmation
	}

	@setup.Getter("getAllFields")
	public fields!: CaseColumn[]

	private cadenceSchedules: ScheduleQueue[] = [];
	private templates: Template[] = []
	private severities: Severity[] = []
	private areas: Area[] = []
	private tags: Tag[] = []
	// If filterOutCaseColumns is updated please also update the ScheduleService Api
	private filterOutCaseColumns = ["created", "closed", "openForDays", "isClosed", "templateId", "caseNumber", "lastUpdated"]
	private loading: boolean = true
	public filter: string = "";

	private get columns () : any[] {
		return [
			{
				key: "schedule.title",
				label: "Title",
				sortable: true
			},
			{
				key: "processAt",
				label: "Due Date",
				sortable: true,
				dataType: "date"
			},
			{
				key: "schedule.templateTitle",
				label: "Template",
				sortable: true
			},
			{
				key: "fields",
				label: "Fields"
			},
			{
				key: "clear",
				label: ""
			}
		]
	}

	private async mounted () {
		this.loading = true
		this.cadenceSchedules = await api.retrieveCadenceSchedules()
		this.loading = false
	}

	private async loadSchedules (): Promise<void> {
		this.cadenceSchedules = await api.retrieveCadenceSchedules()
	}

	/**
	 * Gets a list of the data and value of each field for a schedule
	 */
	private getFieldValues (schedule : Schedule) {
		if (!schedule.fields) {
			return []
		}

		const mappedFields: any[] = []
		for (let key in schedule.fields) {
			var fieldData = this.fields.find(x => x.key === key || (x.dynamicField && x.key === "fields." + key))
			if (fieldData && fieldData.dataType !== "calculated") {
				mappedFields.push({
					fieldData: fieldData,
					value: this.formatFieldValue(schedule, key, fieldData, schedule.fields[key])
				})
			}
		}

		return mappedFields
	}

	/**
	 * Formats the fields value based in its data
	 */
	private formatFieldValue (schedule: Schedule, key: string, fieldData: any, fieldValue: any) {
		if (fieldValue === null) {
			return ""
		}

		if (schedule.fieldLabels && schedule.fieldLabels[key] !== undefined) {
			return schedule.fieldLabels[key]
		}

		// Format built in field values
		switch (fieldData.key) {
			case "severityId":
				var severity = this.severities.find(s => s.id === fieldValue)
				return severity ? severity.title : ""
			case "areas":
				var areas: string[] = fieldValue.map((areaId: string) => {
					var area = this.areas.find(a => a.id === areaId)
					return area ? area.title : null
				})
				return areas.filter(a => a !== null).toString()
			case "tags":
				var tags: string[] = fieldValue.map((tagId: string) => {
					var tag = this.tags.find(t => t.id === tagId)
					return tag ? tag.title : null
				})
				return tags.filter(t => t !== null).toString()
		}

		return formatFieldsMixin.methods.formatFieldValue(fieldData.dataType, fieldValue.value, [])
	}

	/**
	 * Show a formatted value of the Process At Datetime
	 */
	private formatProcessAt (processAtDateTime: Date) {
		// Add one minute to the value so that it shows the start the next hour
		var formattedValue = moment(processAtDateTime).add(1, "minute").toDate()
		// convert time to local timezone
		return formatDateMixin.methods.displayDateFromISOString(formattedValue)
	}

	private cancelCadenceSchedule (schedule: ScheduleQueue) {
		this.$refs.confirmModal.open(schedule.schedule.title, () => {
			this.deleteConfirmed(schedule)
		})
	}

	private async deleteConfirmed (schedule: ScheduleQueue): Promise<void> {
		await api.deleteScheduleQueue(schedule)

		// Refresh Cadence Schedules
		this.cadenceSchedules = await api.retrieveCadenceSchedules()
	}
}

